import { Avatar } from '@crehana/compass.ui/v3/avatar';
import {
  createColumns,
  DataTable,
  useTableState,
} from '@crehana/compass.ui/v3/table';

import type { TTeamMember } from '../hooks/use-team-last-activity';
import { useTeamLastActivity } from '../hooks/use-team-last-activity';
import type { TKeys } from '../i18n';
import { useTranslation } from '../i18n';
import type { TWidgetSize } from '../types';
import { DateTimeDisplay } from './date-time-display';
import { TableErrorState, TableNoDataState } from './table-state';

type TColumnId =
  | 'fullName'
  | 'shiftName'
  | 'currentEventStatus'
  | 'scheduledTime';

const COLUMNS_BY_SIZE: Record<TWidgetSize, TColumnId[]> = {
  l: ['fullName', 'shiftName', 'currentEventStatus', 'scheduledTime'],
  m: ['fullName', 'currentEventStatus', 'scheduledTime'],
  s: ['fullName', 'currentEventStatus'],
};

type TLastStatus = NonNullable<TTeamMember['last_status']>;

const STATUS_KEYS: Record<TLastStatus, TKeys> = {
  NO_RECORD: 'team_table_status_without_mark',
  ENTRY: 'team_table_status_entry_marked',
  BREAK_START: 'team_table_status_break_start_marked',
  BREAK_END: 'team_table_status_break_end_marked',
  EXIT: 'team_table_status_exit_marked',
  REST_DAY: 'team_table_status_shift_break',
  NO_SHIFT: 'team_table_status_without_shift',
};

function formatTimeTo12h(time24: string): string {
  const [hoursStr, minutesStr] = time24.split(':');
  const hours = Number(hoursStr);
  const period = hours >= 12 ? 'p.m.' : 'a.m.';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${hours12}:${minutesStr} ${period}`;
}

function getScheduledTimeRange(
  shift: TTeamMember['shift'],
): string | undefined {
  if (!shift?.entry_time || !shift?.exit_time) {
    return undefined;
  }

  return `${formatTimeTo12h(shift.entry_time)} - ${formatTimeTo12h(shift.exit_time)}`;
}

function isValidStatus(status: string): status is TLastStatus {
  return status in STATUS_KEYS;
}

function getStatusKey(status: string | null | undefined): TKeys {
  if (!status || !isValidStatus(status)) {
    return 'team_table_status_without_shift';
  }

  return STATUS_KEYS[status];
}

function useTeamColumns(size: TWidgetSize) {
  const { t } = useTranslation();
  const visibleColumns = COLUMNS_BY_SIZE[size];

  const statusFilterOptions: Array<{ label: string; value: string }> = [
    { label: t('team_table_status_without_mark'), value: 'NO_RECORD' },
    { label: t('team_table_status_entry_marked'), value: 'ENTRY' },
    {
      label: t('team_table_status_break_start_marked'),
      value: 'BREAK_START',
    },
    {
      label: t('team_table_status_break_end_marked'),
      value: 'BREAK_END',
    },
    { label: t('team_table_status_exit_marked'), value: 'EXIT' },
    { label: t('team_table_status_shift_break'), value: 'REST_DAY' },
    { label: t('team_table_status_without_shift'), value: 'NO_SHIFT' },
  ];

  const allColumns = createColumns<TTeamMember>(
    ({ column, cells, headers }) => {
      const defs: Record<TColumnId, ReturnType<typeof column.accessor>> = {
        fullName: column.display({
          id: 'fullName',
          header: headers.filterable(t('team_table_column_user'), {
            options: [],
          }),
          cell: cells.text(row => ({
            title: row.user.full_name,
            subtitle: row.user.position?.position_name ?? '',
            leading: <Avatar size="sm" userName={row.user.full_name} />,
          })),
        }),
        shiftName: column.display({
          id: 'shiftName',
          header: headers.filterable(t('team_table_column_shift'), {
            options: [],
          }),
          cell: ({ row }) => (
            <span className="tw-cds-font-body-01-md">
              {row.original.shift?.name || t('team_table_status_without_shift')}
            </span>
          ),
        }),
        currentEventStatus: column.display({
          id: 'currentEventStatus',
          header: headers.filterable(t('team_table_column_status'), {
            options: statusFilterOptions,
          }),
          cell: cells.value(row => t(getStatusKey(row.last_status))),
        }),
        scheduledTime: column.display({
          id: 'scheduledTime',
          header: t('team_table_column_scheduled_time'),
          cell: cells.value(row => {
            if (
              row.last_status === 'REST_DAY' ||
              row.last_status === 'NO_SHIFT'
            ) {
              return t('team_table_scheduled_time_not_applicable');
            }

            return (
              getScheduledTimeRange(row.shift) ??
              t('team_table_scheduled_time_not_applicable')
            );
          }),
        }),
      };

      return visibleColumns.map(id => defs[id]);
    },
  );

  return allColumns;
}

type TWidgetTeamProps = {
  timeZone: string;
  size: TWidgetSize;
};
export const WidgetTeam = ({ timeZone, size }: TWidgetTeamProps) => {
  const { t, languageCode } = useTranslation();
  const { data, loading, error, refetch } = useTeamLastActivity();
  const columns = useTeamColumns(size);
  const tableState = useTableState();

  const getTableEmptyState = () => {
    if (error) {
      return (
        <TableErrorState
          onClickButton={() => refetch()}
          title={t('team_table_error_title')}
          description={t('team_table_error_description')}
          buttonLabel={t('team_table_error_retry')}
        />
      );
    }

    return (
      <TableNoDataState
        buttonLabel=""
        title={t('team_table_empty_title')}
        description={t('team_table_empty_description')}
      />
    );
  };

  return (
    <div className="tw-grid tw-grid-cols-12 tw-gap-2.4 tw-h-full">
      <div className="tw-col-span-3 tw-hidden md:tw-block">
        <DateTimeDisplay loading={loading} timeZone={timeZone} />
      </div>
      <div className="tw-col-span-12 md:tw-col-span-9 tw-h-full tw-max-h-full tw-overflow-y-auto tw-min-h-0">
        <DataTable
          columns={columns}
          data={data}
          rowCount={data.length}
          state={tableState}
          loading={loading}
          density="compact"
          locale={languageCode}
          emptyState={getTableEmptyState()}
          getRowId={row => String(row.user_id)}
          cellClassName={() => 'sm:tw-max-w-[16.2rem] md:tw-max-w-full'}
          classNames={{
            table: 'tw-overflow-x-auto tw-min-w-0',
            wrapper: 'tw-w-full tw-min-w-0',
          }}
        />
      </div>
    </div>
  );
};
